/**
 * acmEditor.ts — lógica pura do editor de portas ACM (sem React, sem DOM).
 *
 * O editor não guarda geometria: guarda a ESPECIFICAÇÃO da porta (medidas do vão + `LayoutPorta`) e deixa o
 * motor (calcularPorta) recalcular tudo. Cada edição é só uma nova especificação, então desfazer/refazer
 * é uma pilha de instantâneos (ideia do CADCraft: undo por snapshot barato).
 *
 * Também mora aqui o modelo de "alças" (grips do CADCraft): cada elemento selecionado expõe pontos que o
 * usuário arrasta, e `aplicarAlca` traduz o ponto arrastado de volta em parâmetros do motor.
 */
import type { AcmParams } from './acmGenerator';
import { CAVA, LARGURAS_CHAPA, CHAPA_TIPOS_PADRAO, DETALHES_PADRAO, MODELOS, RIPADA_ALTURA_MAX, ChapaTipoEntrada, DetalhesFolha, FrisoEntrada, calcularPorta } from './calculoAcm';

type Porta = any; // resultado de calcularPorta()

// ------------------------------------------------------------------ layout (parte editável salva na porta)

export interface LayoutPorta {
    /** Campo entre o montante da borda e o montante da cava (onde a cava fica). */
    cavaAfastamento?: number;
    cavaVao?: number;
    /** Cor própria da cava em cada lado (sobre a cor do orçamento). */
    cavaCorA?: string;
    cavaCorB?: string;
    /** Junta única (formato antigo): faixa (borda de referência → junta), vão livre e friso. Use `frisos`. */
    juntaFaixa?: number;
    juntaVao?: number;
    juntaFriso?: number;
    /** Divisões manuais de chapa: x em mm a partir da borda do puxador/fechadura da folha. */
    divisoes?: number[];
    /** Frisos (juntas rebaixadas) verticais e horizontais. Quando existe, substitui a junta única. */
    frisos?: FrisoEntrada[];
    /** `false`: o motor não insere frisos para unir chapas maiores que a comercial (só avisa). */
    frisoAuto?: boolean;
    /** Largura da chapa de ACM (1220, 1250 ou 1500); sem ela o motor escolhe a melhor. */
    larguraChapa?: number;
    /** Alturas (do piso da folha) das travessas intermediárias editadas; quando existe, substitui as automáticas. */
    travessas?: number[];
    /** Montantes extras: x do centro, a partir da borda do puxador/fechadura. */
    montantesExtras?: number[];
    /** Tipo de cada chapa (chave: lado + rótulo, ex.: 'A2', 'B1.2') e o tipo padrão das demais. */
    chapaTipos?: Record<string, ChapaTipoEntrada>;
    chapaPadrao?: ChapaTipoEntrada;
    /** Ripada: puxador integrado (ripa alta) ligado ou desligado. */
    puxadorRipa?: boolean;
    /** Conjunto: variação (dupla, bandeira, fixo lateral), medidas do painel fixo, folha espelhada e detalhes das outras folhas. */
    variacao?: 'dupla' | 'bandeira' | 'fixo-lateral';
    bandeiraAltura?: number;
    fixoLargura?: number;
    fixoLado?: 'esq' | 'dir';
    espelhar?: boolean;
    folhas?: Record<string, DetalhesFolha>;
}

/** Detalhes de uma folha além da principal: o mesmo vocabulário do layout, mais `cava` e as cores próprias. */
export function sanitizarDetalhesFolha(bruto: unknown): DetalhesFolha {
    if (!bruto || typeof bruto !== 'object') return {};
    const o = bruto as Record<string, unknown>;
    const { variacao: _v, bandeiraAltura: _b, fixoLargura: _f, fixoLado: _l, espelhar: _e, folhas: _fo, ...leaf } = sanitizarLayout(bruto) as any;
    const det: any = { ...leaf };
    if (typeof o.cava === 'boolean') det.cava = o.cava;
    if (o.cores && typeof o.cores === 'object' && !Array.isArray(o.cores)) {
        const cores: Record<string, string> = {};
        Object.entries(o.cores as Record<string, unknown>).slice(0, 60).forEach(([k, v]) => { if (typeof v === 'string' && v && /^[A-Za-z][\w.]{0,12}$/.test(k)) cores[k] = v.slice(0, 60); });
        if (Object.keys(cores).length) det.cores = cores;
    }
    return det;
}

/** Descarta valores inválidos de um tipo de chapa; `lisa` sem parâmetros vira `undefined`. */
export function sanitizarChapaTipo(t: any): ChapaTipoEntrada | undefined {
    if (!t || typeof t !== 'object') return undefined;
    if (t.tipo === 'ripada') {
        const ripa = numero(t.ripa), vao = numero(t.vao);
        return { tipo: 'ripada', ...(ripa != null ? { ripa } : {}), ...(vao != null ? { vao } : {}),
                 ...(typeof t.fundo === 'string' && t.fundo ? { fundo: t.fundo.slice(0, 60) } : {}) };
    }
    if (t.tipo === 'frisada') {
        const passo = numero(t.passo);
        return { tipo: 'frisada', ...(passo != null ? { passo } : {}), ...(t.direcao === 'horizontal' ? { direcao: 'horizontal' as const } : {}) };
    }
    return t.tipo === 'lisa' ? { tipo: 'lisa' } : undefined;
}

export const MAX_TRAVESSAS = 24;
export const MAX_MONTANTES_EXTRAS = 8;
export const MAX_DIVISOES = 12;
export const MAX_FRISOS = 8;

const CAMPOS_NUM = ['cavaAfastamento', 'cavaVao', 'juntaFaixa', 'juntaVao', 'juntaFriso'] as const;
const CAMPOS_TEXTO = ['cavaCorA', 'cavaCorB'] as const;

const numero = (v: unknown): number | null => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** Lê o layout guardado na porta do lead, descartando qualquer coisa que não seja valor válido. */
export function sanitizarLayout(bruto: unknown): LayoutPorta {
    const l: LayoutPorta = {};
    if (!bruto || typeof bruto !== 'object') return l;
    const o = bruto as Record<string, unknown>;
    CAMPOS_NUM.forEach(c => { const n = numero(o[c]); if (n != null) l[c] = n; });
    CAMPOS_TEXTO.forEach(c => { if (typeof o[c] === 'string' && o[c]) l[c] = (o[c] as string).slice(0, 60); });
    if (Array.isArray(o.divisoes)) {
        const xs = o.divisoes.map(Number).filter(Number.isFinite).slice(0, MAX_DIVISOES);
        if (xs.length) l.divisoes = [...new Set(xs)].sort((a, b) => a - b);
    }
    const padrao = sanitizarChapaTipo(o.chapaPadrao);
    if (padrao) l.chapaPadrao = padrao; // 'lisa' explícita vale: numa porta ripada, é o "sem ripas"
    if (typeof o.puxadorRipa === 'boolean') l.puxadorRipa = o.puxadorRipa;
    if (o.chapaTipos && typeof o.chapaTipos === 'object' && !Array.isArray(o.chapaTipos)) {
        const tipos: Record<string, ChapaTipoEntrada> = {};
        Object.entries(o.chapaTipos as Record<string, unknown>).slice(0, 80).forEach(([k, v]) => {
            const t = sanitizarChapaTipo(v);
            if (t && /^[AB][\w.]{1,12}$/.test(k)) tipos[k] = t;
        });
        if (Object.keys(tipos).length) l.chapaTipos = tipos;
    }
    if (o.variacao === 'dupla' || o.variacao === 'bandeira' || o.variacao === 'fixo-lateral') l.variacao = o.variacao;
    { const b = numero(o.bandeiraAltura); if (b != null) l.bandeiraAltura = b; }
    { const f = numero(o.fixoLargura); if (f != null) l.fixoLargura = f; }
    if (o.fixoLado === 'esq' || o.fixoLado === 'dir') l.fixoLado = o.fixoLado;
    if (typeof o.espelhar === 'boolean') l.espelhar = o.espelhar;
    if (o.folhas && typeof o.folhas === 'object' && !Array.isArray(o.folhas)) {
        const folhas: Record<string, DetalhesFolha> = {};
        (['f2', 'fixo'] as const).forEach(id => {
            const det = sanitizarDetalhesFolha((o.folhas as Record<string, unknown>)[id]);
            if (Object.keys(det).length) folhas[id] = det;
        });
        if (Object.keys(folhas).length) l.folhas = folhas;
    }
    if (Array.isArray(o.travessas)) l.travessas = o.travessas.map(Number).filter(Number.isFinite).slice(0, MAX_TRAVESSAS).sort((a, b) => a - b);
    if (Array.isArray(o.montantesExtras)) {
        const xs = o.montantesExtras.map(Number).filter(Number.isFinite).slice(0, MAX_MONTANTES_EXTRAS);
        if (xs.length) l.montantesExtras = [...new Set(xs)].sort((a, b) => a - b);
    }
    if (Array.isArray(o.frisos)) {
        l.frisos = o.frisos.slice(0, MAX_FRISOS).flatMap((f: any): FrisoEntrada[] => {
            const posicao = numero(f?.posicao);
            if (posicao == null) return [];
            const vao = numero(f?.vao), friso = numero(f?.friso);
            return [{
                orientacao: f?.orientacao === 'horizontal' ? 'horizontal' : 'vertical', posicao,
                ...(vao != null ? { vao } : {}), ...(friso != null ? { friso } : {}),
                ...(typeof f?.cor === 'string' && f.cor ? { cor: f.cor.slice(0, 60) } : {}),
                ...(f?.auto === true ? { auto: true } : {}),
            }];
        });
    }
    if (o.frisoAuto === false) l.frisoAuto = false;
    if ((LARGURAS_CHAPA as readonly number[]).includes(Number(o.larguraChapa))) l.larguraChapa = Number(o.larguraChapa);
    return l;
}

/** Layout atual de uma especificação (só o que foi definido). */
export function layoutDeParams(p: Partial<AcmParams>): LayoutPorta {
    return sanitizarLayout({
        cavaAfastamento: p.cavaAfastamento, cavaVao: p.cavaVao, cavaCorA: p.cavaCorA, cavaCorB: p.cavaCorB,
        juntaFaixa: p.juntaFaixa, juntaVao: p.juntaVao, juntaFriso: p.juntaFriso, divisoes: p.divisoes, frisos: p.frisos, frisoAuto: p.frisoAuto, larguraChapa: p.larguraChapa,
        travessas: p.travessas, montantesExtras: p.montantesExtras,
        chapaTipos: p.chapaTipos, chapaPadrao: p.chapaPadrao, puxadorRipa: p.puxadorRipa,
        variacao: p.variacao, bandeiraAltura: p.bandeiraAltura, fixoLargura: p.fixoLargura, fixoLado: p.fixoLado, espelhar: p.espelhar, folhas: p.folhas,
    });
}

/** Campos do motor correspondentes a um layout salvo. */
export function layoutParaParams(l?: LayoutPorta | null): Partial<AcmParams> {
    return l ? { ...sanitizarLayout(l) } : {};
}

export function aplicarLayout(p: AcmParams, l: LayoutPorta): AcmParams {
    const limpo: AcmParams = { ...p };
    CAMPOS_NUM.forEach(c => { delete limpo[c]; });
    CAMPOS_TEXTO.forEach(c => { delete limpo[c]; });
    delete limpo.divisoes;
    delete limpo.frisos;
    delete limpo.frisoAuto;
    delete limpo.larguraChapa;
    delete limpo.travessas;
    delete limpo.montantesExtras;
    delete limpo.chapaTipos;
    delete limpo.chapaPadrao;
    delete limpo.puxadorRipa;
    delete limpo.variacao; delete limpo.bandeiraAltura; delete limpo.fixoLargura; delete limpo.fixoLado; delete limpo.espelhar; delete limpo.folhas;
    return { ...limpo, ...layoutParaParams(l) };
}

/** Volta os detalhes (cava, frisos, perfis, chapas, divisões) ao padrão do Método ACM; a variação do conjunto continua. */
export function layoutPadrao(p: AcmParams): AcmParams {
    const conjunto = { variacao: p.variacao, bandeiraAltura: p.bandeiraAltura, fixoLargura: p.fixoLargura, fixoLado: p.fixoLado, espelhar: p.espelhar };
    const limpo = aplicarLayout(p, {});
    (Object.keys(conjunto) as Array<keyof typeof conjunto>).forEach(k => { if (conjunto[k] !== undefined) (limpo as any)[k] = conjunto[k]; });
    return limpo;
}

export function layoutIgual(a: LayoutPorta, b: LayoutPorta): boolean {
    return JSON.stringify(sanitizarLayout(a)) === JSON.stringify(sanitizarLayout(b));
}

// ------------------------------------------------------------------ histórico (desfazer / refazer)

export interface Historico<T> { passado: T[]; presente: T; futuro: T[] }

const LIMITE_HISTORICO = 100;

export const novoHistorico = <T,>(inicial: T): Historico<T> => ({ passado: [], presente: inicial, futuro: [] });

/** Empilha um novo estado; edições idênticas ao estado atual não entram no histórico. */
export function empurrar<T>(h: Historico<T>, novo: T): Historico<T> {
    if (JSON.stringify(novo) === JSON.stringify(h.presente)) return h;
    return { passado: [...h.passado, h.presente].slice(-LIMITE_HISTORICO), presente: novo, futuro: [] };
}

export function desfazer<T>(h: Historico<T>): Historico<T> {
    if (h.passado.length === 0) return h;
    const passado = h.passado.slice(0, -1);
    return { passado, presente: h.passado[h.passado.length - 1], futuro: [h.presente, ...h.futuro] };
}

export function refazer<T>(h: Historico<T>): Historico<T> {
    if (h.futuro.length === 0) return h;
    return { passado: [...h.passado, h.presente], presente: h.futuro[0], futuro: h.futuro.slice(1) };
}

// ------------------------------------------------------------------ seleção e alças

export type Selecao =
    | { tipo: 'porta' }
    | { tipo: 'cava' }
    | { tipo: 'friso'; indice: number }
    | { tipo: 'travessa'; indice: number }
    | { tipo: 'montante'; indice: number }
    /** `lado`: qual face da chapa está em foco (a chapa é a mesma nos dois lados, mas cor e tipo são de cada lado). */
    | { tipo: 'chapa'; rotulo: string; lado?: 'A' | 'B' }
    | { tipo: 'divisao'; indice: number };

export type AlcaId =
    | 'cava-mover' | 'cava-esq' | 'cava-dir' | 'cava-pegador'
    | `friso-mover-${number}` | `friso-vao-${number}` | `friso-aparente-${number}`
    | `travessa-mover-${number}` | `montante-mover-${number}`
    | 'fechadura' | 'travada'
    | `divisao-${number}`;

export interface Alca {
    id: AlcaId;
    /** Posição na folha, em mm: x a partir da borda esquerda, y a partir do topo da folha. */
    x: number;
    y: number;
    rotulo: string;
    eixo: 'x' | 'y';
    /** Alças travadas (borda lateral da chapa, fixa pelo Método) aparecem, mas não arrastam. */
    travada?: boolean;
}

/** Mesma seleção? O lado em foco de uma chapa não conta (é a mesma chapa nos dois lados). */
export const mesmaSelecao = (a: Selecao | null, b: Selecao | null) => {
    if (!a || !b) return false;
    const semLado = (s: Selecao) => (s.tipo === 'chapa' ? { tipo: s.tipo, rotulo: s.rotulo } : s);
    return JSON.stringify(semLado(a)) === JSON.stringify(semLado(b));
};

const zona = (d: Porta, tipo: string) => d.zonas.find((z: any) => z.tipo === tipo);

/** Divisões manuais aplicadas (ordem crescente de x). */
export const divisoesDe = (d: Porta): number[] => [...(d.entrada.divisoes || [])].sort((a: number, b: number) => a - b);

/** Frisos da porta, no formato de entrada do motor (a lista é a fonte da verdade depois da primeira edição). */
export const frisosDe = (d: Porta): FrisoEntrada[] => (d.entrada.frisos || []).map((f: FrisoEntrada) => ({ ...f }));

/** Aplica uma nova lista de frisos e tira da especificação os campos da junta única (que a lista substitui). */
function comFrisos(p: AcmParams, lista: FrisoEntrada[]): AcmParams {
    const novo: AcmParams = { ...p, frisos: lista, junta: lista.length > 0 };
    delete novo.juntaFaixa; delete novo.juntaVao; delete novo.juntaFriso;
    return novo;
}

/** Montantes da folha (zonas), na ordem da esquerda para a direita. `papel`: borda, cava, friso ou extra. */
export const montantesDe = (d: Porta): any[] => d.zonas.filter((z: any) => z.tipo === 'montante');

/** Alturas (do piso da folha) das travessas intermediárias que não pertencem a um friso horizontal. */
export const travessasDe = (d: Porta): number[] =>
    (d.travessas.itens as any[]).filter(t => t.friso == null).map(t => d.folha.altura - t.centro);

/** Campo mais largo (para posicionar um perfil novo no maior espaço livre). */
const maiorCampo = (d: Porta) => [...d.zonas.filter((z: any) => z.tipo === 'campo')].sort((a: any, b: any) => b.largura - a.largura)[0];

export const nomeFriso = (f: { orientacao: string }, i: number) => `Friso ${i + 1} (${f.orientacao})`;

/** Pontos arrastáveis do elemento selecionado. */
export function gerarAlcas(d: Porta, sel: Selecao | null): Alca[] {
    if (!sel) return [];
    const FA = d.folha.altura;
    const FL = d.folha.largura;
    const alcas: Alca[] = [];
    const yMover = FA * 0.32, yBorda = FA * 0.5;

    // Painel fixo não tem fechadura
    if (sel.tipo === 'porta' && d.resumo.ferragem.tipo !== 'fixa') {
        alcas.push({ id: 'fechadura', x: 0, y: FA - d.entrada.fechaduraEixo, rotulo: 'Altura da fechadura', eixo: 'y' });
    }
    if (sel.tipo === 'cava') {
        const z = zona(d, 'cava');
        if (z) {
            alcas.push({ id: 'cava-mover', x: (z.x0 + z.x1) / 2, y: yMover, rotulo: 'Mover cava', eixo: 'x' });
            alcas.push({ id: 'cava-esq', x: z.x0, y: yBorda, rotulo: 'Borda esquerda da cava', eixo: 'x' });
            alcas.push({ id: 'cava-dir', x: z.x1, y: yBorda, rotulo: 'Borda direita da cava', eixo: 'x' });
        }
    }
    if (sel.tipo === 'friso') {
        const f = d.frisos[sel.indice];
        if (f) {
            if (f.orientacao === 'vertical') {
                alcas.push({ id: `friso-mover-${f.indice}`, x: f.centro, y: yMover, rotulo: 'Mover friso', eixo: 'x' });
                alcas.push({ id: `friso-vao-${f.indice}`, x: f.centro - f.vaoLivre / 2, y: yBorda, rotulo: 'Vão do friso', eixo: 'x' });
                alcas.push({ id: `friso-vao-${f.indice}`, x: f.centro + f.vaoLivre / 2, y: yBorda, rotulo: 'Vão do friso', eixo: 'x' });
            } else {
                alcas.push({ id: `friso-mover-${f.indice}`, x: FL * 0.28, y: f.centro, rotulo: 'Mover friso', eixo: 'y' });
                alcas.push({ id: `friso-vao-${f.indice}`, x: FL * 0.5, y: f.centro - f.vaoLivre / 2, rotulo: 'Vão do friso', eixo: 'y' });
                alcas.push({ id: `friso-vao-${f.indice}`, x: FL * 0.5, y: f.centro + f.vaoLivre / 2, rotulo: 'Vão do friso', eixo: 'y' });
            }
        }
    }
    if (sel.tipo === 'divisao') {
        const x = divisoesDe(d)[sel.indice];
        if (x != null) alcas.push({ id: `divisao-${sel.indice}`, x, y: yBorda, rotulo: 'Divisão da chapa', eixo: 'x' });
    }
    if (sel.tipo === 'travessa') {
        const t = d.travessas.itens[sel.indice];
        const campo = maiorCampo(d);
        // Só as travessas soltas se movem; as dos frisos horizontais andam com o friso
        if (t && t.friso == null) alcas.push({ id: `travessa-mover-${sel.indice}`, x: campo ? (campo.x0 + campo.x1) / 2 : FL / 2, y: t.centro, rotulo: 'Mover travessa', eixo: 'y' });
    }
    if (sel.tipo === 'montante') {
        const z = montantesDe(d)[sel.indice];
        if (z && z.papel === 'extra') alcas.push({ id: `montante-mover-${z.extra}`, x: (z.x0 + z.x1) / 2, y: FA * 0.4, rotulo: 'Mover montante', eixo: 'x' });
    }
    if (sel.tipo === 'chapa') {
        const b = d.bandejas.find((q: any) => q.rotulo === sel.rotulo);
        if (b) {
            const yMeio = (b.y0 + b.y1) / 2, xMeio = (b.x0 + b.x1) / 2;
            const lateral = (borda: string, x: number, friso: number | null): Alca => {
                if (borda === 'pegador') return { id: 'cava-pegador', x, y: yMeio, rotulo: 'Início da cava (pegador)', eixo: 'x' };
                if (borda === 'cava') return { id: 'cava-dir', x, y: yMeio, rotulo: 'Fim da cava', eixo: 'x' };
                if (borda === 'junta' && friso != null) return { id: `friso-aparente-${friso}`, x, y: yMeio, rotulo: 'Friso aparente', eixo: 'x' };
                if (borda === 'topo') {
                    const folga = DETALHES_PADRAO.FOLGA_DIVISAO;
                    const i = divisoesDe(d).findIndex(v => Math.abs(v - x) <= folga / 2 + 0.01);
                    return { id: `divisao-${Math.max(i, 0)}`, x, y: yMeio, rotulo: 'Divisão da chapa', eixo: 'x' };
                }
                return { id: 'travada', x, y: yMeio, rotulo: 'Borda lateral (fixa pelo Método)', eixo: 'x', travada: true };
            };
            alcas.push(lateral(b.bordaEsq, b.x0, b.frisoEsq), lateral(b.bordaDir, b.x1, b.frisoDir));
            // As pontas da folha não se editam: só as bordas horizontais que são friso entram
            if (b.bordaCima === 'friso' && b.frisoCima != null) alcas.push({ id: `friso-aparente-${b.frisoCima}`, x: xMeio, y: b.y0, rotulo: 'Friso aparente', eixo: 'y' });
            if (b.bordaBaixo === 'friso' && b.frisoBaixo != null) alcas.push({ id: `friso-aparente-${b.frisoBaixo}`, x: xMeio, y: b.y1, rotulo: 'Friso aparente', eixo: 'y' });
        }
    }
    return alcas;
}

// ------------------------------------------------------------------ edição por alça

const arred = (v: number, passo: number) => (passo > 0 ? Math.round(v / passo) * passo : v);
const limitar = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

export interface OpcoesAlca { passo?: number }

/**
 * Traduz a alça arrastada (nova posição x/y na folha) em uma nova especificação.
 * Nunca muta `p`; devolve `p` inalterado quando a alça não se aplica.
 */
export function aplicarAlca(p: AcmParams, d: Porta, id: AlcaId, pos: { x: number; y: number }, opts: OpcoesAlca = {}): AcmParams {
    const passo = opts.passo ?? 1;
    const dd: number = d.modelo.d;
    const FL: number = d.folha.largura;
    const FA: number = d.folha.altura;
    const x = arred(pos.x, passo);

    if (id === 'fechadura') {
        return { ...p, fechaduraEixo: limitar(arred(FA - pos.y, passo), 100, FA - 100) };
    }
    if (id === 'cava-mover' || id === 'cava-esq' || id === 'cava-dir' || id === 'cava-pegador') {
        const z = zona(d, 'cava');
        if (!z) return p;
        let x0 = z.x0, vao = z.largura;
        if (id === 'cava-mover') x0 = x - vao / 2;
        // 'cava-pegador' é a borda da chapa que avança CAVA.aba sobre a boca da cava: a cava começa aba antes
        if (id === 'cava-esq' || id === 'cava-pegador') {
            x0 = Math.min(id === 'cava-pegador' ? x - CAVA.aba : x, z.x1 - 2 * CAVA.aba);
            vao = z.x1 - x0;
        }
        if (id === 'cava-dir') vao = Math.max(x - z.x0, 2 * CAVA.aba);
        const afast = Math.max(0, arred(x0 - 2 * dd, passo));
        return { ...p, cavaAfastamento: afast, cavaVao: arred(vao, passo) };
    }
    const mf = /^friso-(mover|vao|aparente)-(\d+)$/.exec(id);
    if (mf) {
        const lista = frisosDe(d);
        const f = d.frisos[Number(mf[2])];
        if (!f) return p;
        const horizontal = f.orientacao === 'horizontal';
        const coord = horizontal ? arred(pos.y, passo) : x; // y é medido do topo da folha
        const c: number = f.centro;
        let { vaoLivre: vao, friso } = f;
        let posicao: number = f.posicao;
        if (mf[1] === 'mover') posicao = horizontal ? FA - coord : coord;
        if (mf[1] === 'vao') vao = Math.max(friso + 2, arred(2 * Math.abs(c - coord), passo));
        if (mf[1] === 'aparente') friso = limitar(arred(2 * Math.abs(c - coord), passo), 2, Math.max(2, vao - 2));
        lista[f.indice] = { ...lista[f.indice], posicao, vao, friso };
        return comFrisos(p, lista);
    }
    const mt = /^travessa-mover-(\d+)$/.exec(id);
    if (mt) {
        const t = d.travessas.itens[Number(mt[1])];
        if (!t || t.friso != null) return p;
        const lista = travessasDe(d);
        const atual = FA - t.centro;
        const k = lista.findIndex(h => Math.abs(h - atual) < 0.001);
        if (k < 0) return p;
        lista[k] = limitar(FA - arred(pos.y, passo), dd * 1.5, FA - dd * 1.5);
        return { ...p, travessas: lista };
    }
    const mm = /^montante-mover-(\d+)$/.exec(id);
    if (mm) {
        const k = Number(mm[1]);
        const extras: number[] = [...(d.entrada.montantesExtras || [])];
        if (k >= extras.length) return p;
        const min = k > 0 ? extras[k - 1] + dd + 1 : dd * 1.5;
        const max = k < extras.length - 1 ? extras[k + 1] - dd - 1 : FL - dd * 1.5;
        extras[k] = limitar(x, min, max);
        return { ...p, montantesExtras: extras };
    }
    const m = /^divisao-(\d+)$/.exec(id);
    if (m) {
        const i = Number(m[1]);
        const divs = divisoesDe(d);
        if (i >= divs.length) return p;
        const folga = DETALHES_PADRAO.FOLGA_DIVISAO + 1;
        const min = i > 0 ? divs[i - 1] + folga : 0;
        const max = i < divs.length - 1 ? divs[i + 1] - folga : FL;
        divs[i] = limitar(x, min, max);
        return { ...p, divisoes: divs };
    }
    return p;
}

// ------------------------------------------------------------------ frisos: criar, remover, editar

/** Vão e friso aparente padrão da linha da porta. */
const padraoFriso = (d: Porta) => ({ vao: MODELOS[d.modelo.id].junta.vao as number, friso: MODELOS[d.modelo.id].junta.friso as number });

/**
 * Acrescenta um friso no maior espaço livre (centro do maior campo, ou da maior altura entre travessas).
 * Devolve a nova especificação e o índice do friso criado.
 */
export function adicionarFriso(p: AcmParams, d: Porta, orientacao: 'vertical' | 'horizontal'): { params: AcmParams; indice: number } | null {
    const lista = frisosDe(d);
    if (lista.length >= MAX_FRISOS) return null;
    const FA: number = d.folha.altura, FL: number = d.folha.largura, dd: number = d.modelo.d;
    const { vao, friso } = padraoFriso(d);
    let posicao: number;
    if (orientacao === 'vertical') {
        const livres = d.zonas.filter((z: any) => z.tipo === 'campo').sort((a: any, b: any) => b.largura - a.largura);
        posicao = livres.length ? Math.round((livres[0].x0 + livres[0].x1) / 2) : Math.round(FL / 2);
    } else {
        // maior altura livre entre a travessa de cima, os outros frisos horizontais e a de baixo
        const cortes = (d.frisos as any[]).filter(f => f.orientacao === 'horizontal').sort((a, b) => a.centro - b.centro);
        const limites = [dd, ...cortes.flatMap(f => [f.centro - dd / 2, f.centro + dd / 2]), FA - dd];
        let melhor = { tam: -1, centro: FA / 2 };
        for (let i = 0; i < limites.length; i += 2) {
            const tam = limites[i + 1] - limites[i];
            if (tam > melhor.tam) melhor = { tam, centro: (limites[i] + limites[i + 1]) / 2 };
        }
        posicao = Math.round(FA - melhor.centro);
    }
    lista.push({ orientacao, posicao, vao, friso });
    return { params: comFrisos(p, lista), indice: lista.length - 1 };
}

export function removerFriso(p: AcmParams, d: Porta, indice: number): AcmParams {
    const removido = frisosDe(d)[indice];
    const novo = comFrisos(p, frisosDe(d).filter((_, i) => i !== indice));
    // Apagar um friso automático desliga a automação nesta porta: a chapa grande fica só com o aviso
    return removido?.auto ? { ...novo, frisoAuto: false } : novo;
}

/** Troca campos de um friso (orientação, posição, vão, friso aparente, cor). */
export function editarFriso(p: AcmParams, d: Porta, indice: number, mudancas: Partial<FrisoEntrada>): AcmParams {
    const lista = frisosDe(d);
    if (!lista[indice]) return p;
    // Editar um friso automático o torna manual (o motor não o recalcula mais)
    const { auto: _auto, ...manual } = lista[indice];
    lista[indice] = { ...manual, ...mudancas };
    return comFrisos(p, lista);
}

// ------------------------------------------------------------------ perfis da estrutura: travessas e montantes extras

/** Acrescenta uma travessa no meio do maior vão entre travessas. Passa a lista de travessas para manual. */
export function adicionarTravessa(p: AcmParams, d: Porta): { params: AcmParams; indice: number } | null {
    const lista = travessasDe(d);
    if (lista.length >= MAX_TRAVESSAS) return null;
    const FA: number = d.folha.altura, dd: number = d.modelo.d;
    const itens = d.travessas.itens as any[];
    const membros = [{ bottom: dd }, ...itens.map(t => ({ top: t.top, bottom: t.top + dd })), { top: FA - dd, bottom: FA }] as Array<{ top?: number; bottom: number }>;
    let melhor = { tam: -1, meio: FA / 2 };
    for (let i = 0; i < membros.length - 1; i++) {
        const ini = membros[i].bottom, fim = membros[i + 1].top as number;
        if (fim - ini > melhor.tam) melhor = { tam: fim - ini, meio: (ini + fim) / 2 };
    }
    const altura = Math.round(FA - melhor.meio);
    lista.push(altura);
    const topo = FA - altura - dd / 2;
    return { params: { ...p, travessas: lista }, indice: itens.filter(t => t.top < topo).length };
}

/** Apaga uma travessa solta (as dos frisos horizontais saem junto com o friso). */
export function removerTravessa(p: AcmParams, d: Porta, indice: number): AcmParams {
    const t = d.travessas.itens[indice];
    if (!t || t.friso != null) return p;
    const atual = d.folha.altura - t.centro;
    return { ...p, travessas: travessasDe(d).filter(h => Math.abs(h - atual) >= 0.001) };
}

/** Acrescenta um montante extra no centro do maior campo. */
export function adicionarMontante(p: AcmParams, d: Porta): { params: AcmParams; indice: number } | null {
    const extras: number[] = [...(d.entrada.montantesExtras || [])];
    if (extras.length >= MAX_MONTANTES_EXTRAS) return null;
    const campo = maiorCampo(d);
    const x = Math.round(campo ? (campo.x0 + campo.x1) / 2 : d.folha.largura / 2);
    extras.push(x);
    extras.sort((a, b) => a - b);
    const novo: AcmParams = { ...p, montantesExtras: extras };
    // índice do novo perfil na lista de montantes (para selecioná-lo em seguida)
    const indice = montantesDe(calcularPorta(novo)).findIndex(z => z.papel === 'extra' && z.extra === extras.indexOf(x));
    return { params: novo, indice };
}

export function removerMontante(p: AcmParams, d: Porta, extraIndice: number): AcmParams {
    const extras: number[] = [...(d.entrada.montantesExtras || [])].filter((_, i) => i !== extraIndice);
    const novo: AcmParams = { ...p };
    delete novo.montantesExtras;
    return extras.length ? { ...novo, montantesExtras: extras } : novo;
}

// ------------------------------------------------------------------ tipo de chapa (lisa, ripada, frisada)

/** Tipo padrão (com as medidas de referência) de cada tipo de chapa, para quando o usuário troca o tipo no painel. */
export function chapaTipoInicial(tipo: ChapaTipoEntrada['tipo']): ChapaTipoEntrada {
    if (tipo === 'ripada') return { tipo, ripa: CHAPA_TIPOS_PADRAO.ripada.ripa, vao: CHAPA_TIPOS_PADRAO.ripada.vao };
    if (tipo === 'frisada') return { tipo, passo: CHAPA_TIPOS_PADRAO.frisada.passo, direcao: 'vertical' };
    return { tipo: 'lisa' };
}

/** Define o tipo de uma chapa de um lado. Se igual ao tipo padrão da porta, tira a entrada própria. */
export function definirTipoChapa(p: AcmParams, lado: 'A' | 'B', rotulo: string, spec: ChapaTipoEntrada): AcmParams {
    const tipos = { ...(p.chapaTipos || {}) };
    const padrao = p.chapaPadrao?.tipo || 'lisa';
    if (spec.tipo === 'lisa' && padrao === 'lisa') delete tipos[lado + rotulo];
    else tipos[lado + rotulo] = spec;
    const novo: AcmParams = { ...p };
    delete novo.chapaTipos;
    return Object.keys(tipos).length ? { ...novo, chapaTipos: tipos } : novo;
}

/** Define o tipo de todas as chapas de uma vez (apaga as escolhas individuais). */
export function definirTipoTodas(p: AcmParams, spec: ChapaTipoEntrada): AcmParams {
    const novo: AcmParams = { ...p };
    delete novo.chapaTipos;
    delete novo.chapaPadrao;
    // Na linha ripada o padrão volta a ser ripada se a escolha sumir: "lisa" precisa ficar registrada
    return spec.tipo === 'lisa' && !p.linhaRipada ? novo : { ...novo, chapaPadrao: spec };
}

// ------------------------------------------------------------------ linha da porta

export type LinhaEditor = 'social' | 'ripada' | 'interna' | 'correr';

/** Linha que a especificação representa (a ripada é a social com chapas ripadas). */
export const linhaDe = (p: AcmParams): LinhaEditor => (p.linhaRipada ? 'ripada' : p.modelo);

/**
 * Troca a linha da porta. Cada linha tem os seus detalhes (cava, frisos, perfis, chapas): trocar volta ao padrão
 * da nova linha, preservando só o vão, as cores e o resto da identificação.
 */
export function trocarLinha(p: AcmParams, linha: LinhaEditor): AcmParams {
    const limpo = layoutPadrao(p);
    delete limpo.linhaRipada;
    delete limpo.alturaMaxima;
    delete limpo.ladoAbertura;
    if (linha === 'correr') { delete limpo.variacao; delete limpo.bandeiraAltura; delete limpo.fixoLargura; delete limpo.fixoLado; delete limpo.espelhar; }
    if (linha === 'ripada') return { ...limpo, modelo: 'social', cava: false, linhaRipada: true, alturaMaxima: RIPADA_ALTURA_MAX };
    return { ...limpo, modelo: linha, cava: linha === 'social' ? p.cava : linha === 'interna' ? p.modelo === 'interna' && !p.linhaRipada && !!p.cava : false };
}

// ------------------------------------------------------------------ divisões de chapa

/** Divide a chapa ao meio (na largura). Devolve a nova especificação e o índice da divisão criada. */
export function dividirChapa(p: AcmParams, d: Porta, rotulo: string): { params: AcmParams; indice: number } | null {
    const b = d.bandejas.find((q: any) => q.rotulo === rotulo);
    const atuais = divisoesDe(d);
    if (!b || atuais.length >= MAX_DIVISOES) return null;
    const x = Math.round((b.x0 + b.x1) / 2);
    const divs = [...atuais, x].sort((a, c) => a - c);
    return { params: { ...p, divisoes: divs }, indice: divs.indexOf(x) };
}

export function removerDivisao(p: AcmParams, d: Porta, indice: number): AcmParams {
    const divs = divisoesDe(d).filter((_, i) => i !== indice);
    const novo: AcmParams = { ...p };
    delete novo.divisoes;
    return divs.length ? { ...novo, divisoes: divs } : novo;
}

// ------------------------------------------------------------------ snaps (ímãs ao arrastar)

export interface AlvoSnap { x: number; rotulo: string }

/** Pontos (em x) onde o cursor "gruda": bordas da folha, montantes fixos, centro e os outros detalhes. */
export function alvosSnap(d: Porta, ignorar: AlcaId | null): AlvoSnap[] {
    const FL: number = d.folha.largura;
    const dd: number = d.modelo.d;
    const alvos: AlvoSnap[] = [
        { x: 0, rotulo: 'borda da folha' }, { x: FL, rotulo: 'borda da folha' },
        { x: 4, rotulo: 'recuo de 4' }, { x: FL - 4, rotulo: 'recuo de 4' },
        { x: dd, rotulo: 'montante da borda' }, { x: FL - dd, rotulo: 'montante da borda' },
        { x: FL / 2, rotulo: 'centro da folha' },
    ];
    const zc = zona(d, 'cava');
    const dragCava = !!ignorar && ignorar.startsWith('cava');
    const friso = ignorar ? /^friso-\w+-(\d+)$/.exec(ignorar) : null;
    const dragFriso = friso ? Number(friso[1]) : -1;
    const montExtra = ignorar ? /^montante-mover-(\d+)$/.exec(ignorar) : null;
    const dragExtra = montExtra ? Number(montExtra[1]) : -1;
    if (zc && !dragCava) alvos.push({ x: zc.x0, rotulo: 'início da cava' }, { x: zc.x1, rotulo: 'fim da cava' }, { x: zc.x0 + CAVA.aba, rotulo: 'pegador' });
    (d.frisos as any[]).filter(f => f.orientacao === 'vertical' && f.indice !== dragFriso).forEach(f => {
        alvos.push({ x: f.centro - f.vaoLivre / 2, rotulo: 'início do friso' }, { x: f.centro + f.vaoLivre / 2, rotulo: 'fim do friso' }, { x: f.centro, rotulo: 'centro do friso' });
    });
    // montantes internos que não pertencem ao detalhe arrastado
    d.zonas.filter((z: any) => z.tipo === 'montante').forEach((z: any) => {
        const daCava = !!zc && (z.x1 === zc.x0 || z.x0 === zc.x1);
        const daJunta = z.isJuntaCenter && z.friso === dragFriso;
        if ((dragCava && daCava) || daJunta || (z.papel === 'extra' && z.extra === dragExtra)) return;
        alvos.push({ x: z.x0, rotulo: 'montante' }, { x: z.x1, rotulo: 'montante' }, { x: (z.x0 + z.x1) / 2, rotulo: 'eixo do montante' });
    });
    divisoesDe(d).forEach((x, i) => { if (ignorar !== `divisao-${i}`) alvos.push({ x, rotulo: 'divisão' }); });
    return alvos;
}

/** Pontos (em y, do topo da folha) para frisos horizontais e a fechadura: travessas da borda, meio e outros frisos. */
export function alvosSnapY(d: Porta, ignorar: AlcaId | null): AlvoSnap[] {
    const FA: number = d.folha.altura;
    const dd: number = d.modelo.d;
    const friso = ignorar ? /^friso-\w+-(\d+)$/.exec(ignorar) : null;
    const dragFriso = friso ? Number(friso[1]) : -1;
    const trav = ignorar ? /^travessa-mover-(\d+)$/.exec(ignorar) : null;
    const dragTrav = trav ? Number(trav[1]) : -1;
    const alvos: AlvoSnap[] = [
        { x: 0, rotulo: 'topo da folha' }, { x: FA, rotulo: 'base da folha' },
        { x: dd, rotulo: 'travessa superior' }, { x: FA - dd, rotulo: 'travessa inferior' },
        { x: FA / 2, rotulo: 'meio da folha' },
    ];
    (d.frisos as any[]).filter(f => f.orientacao === 'horizontal' && f.indice !== dragFriso).forEach(f => {
        alvos.push({ x: f.centro - f.vaoLivre / 2, rotulo: 'início do friso' }, { x: f.centro + f.vaoLivre / 2, rotulo: 'fim do friso' }, { x: f.centro, rotulo: 'centro do friso' });
    });
    (d.travessas.itens as any[]).forEach((t, i) => {
        if (i !== dragTrav) alvos.push({ x: t.centro, rotulo: 'eixo da travessa' }, { x: t.top, rotulo: 'travessa' }, { x: t.top + dd, rotulo: 'travessa' });
    });
    return alvos;
}

/** Gruda em `x` no alvo mais próximo dentro da tolerância (mm). Sem alvo, devolve x. */
export function aplicarSnap(x: number, alvos: AlvoSnap[], tolerancia: number): { x: number; alvo?: AlvoSnap } {
    let melhor: AlvoSnap | undefined;
    let dist = tolerancia;
    alvos.forEach(a => { const dx = Math.abs(a.x - x); if (dx <= dist) { dist = dx; melhor = a; } });
    return melhor ? { x: melhor.x, alvo: melhor } : { x };
}

/**
 * Snap de um detalhe que anda inteiro (cava, friso): o ímã vale para a borda inicial, o centro ou a borda final
 * — o que estiver mais perto de um alvo. `x` é o centro desejado; devolve o centro já ajustado.
 */
export function snapMover(x: number, largura: number, alvos: AlvoSnap[], tolerancia: number): { x: number; alvo?: AlvoSnap } {
    let melhor: { x: number; alvo: AlvoSnap; dist: number } | undefined;
    [-largura / 2, 0, largura / 2].forEach(off => {
        const r = aplicarSnap(x + off, alvos, tolerancia);
        if (r.alvo) {
            const dist = Math.abs(r.x - (x + off));
            if (!melhor || dist < melhor.dist) melhor = { x: r.x - off, alvo: r.alvo, dist };
        }
    });
    return melhor ? { x: melhor.x, alvo: melhor.alvo } : { x };
}

// ------------------------------------------------------------------ seleção a partir do motor

/** A seleção ainda existe nesta porta? (após uma edição a chapa/divisão/friso pode ter sumido). */
export function selecaoValida(d: Porta, sel: Selecao | null): Selecao | null {
    if (!sel) return null;
    if (sel.tipo === 'cava') return zona(d, 'cava') ? sel : null;
    if (sel.tipo === 'friso') return sel.indice < d.frisos.length ? sel : null;
    if (sel.tipo === 'travessa') return sel.indice < d.travessas.itens.length ? sel : null;
    if (sel.tipo === 'montante') return sel.indice < montantesDe(d).length ? sel : null;
    if (sel.tipo === 'chapa') return d.bandejas.some((b: any) => b.rotulo === sel.rotulo) ? sel : null;
    if (sel.tipo === 'divisao') return sel.indice < divisoesDe(d).length ? sel : null;
    return sel;
}

/** Seleção correspondente a um painel do 3D (id de `construirCena3D`): `chapa-A2`, `cava-A-fundo`, `friso-fundo-B-1`. */
export function selecaoDoPainel(id: string): Selecao | null {
    if (/^puxador-ripa-[AB]$/.test(id)) return { tipo: 'chapa', rotulo: '1' };
    const chapa = /^chapa-[AB](.+)$/.exec(id) || /^(?:ripa|fresa)-[AB](.+)-\d+$/.exec(id);
    if (chapa) return { tipo: 'chapa', rotulo: chapa[1] };
    if (id.startsWith('cava-')) return { tipo: 'cava' };
    const friso = /^friso-fundo-[AB]-(\d+)/.exec(id);
    if (friso) return { tipo: 'friso', indice: Number(friso[1]) };
    return null;
}

/**
 * Separa o prefixo de folha de um id de painel 3D: `f2-chapa-A1` → folha 'f2'; `fx-ripa-B2-3` → 'fixo'; sem prefixo → 'f1'.
 */
export function folhaDoPainel(id: string): { folha: 'f1' | 'f2' | 'fixo'; base: string } {
    if (id.startsWith('f2-')) return { folha: 'f2', base: id.slice(3) };
    if (id.startsWith('fx-')) return { folha: 'fixo', base: id.slice(3) };
    return { folha: 'f1', base: id };
}

/** O que um clique num painel do 3D seleciona: o elemento e a folha a que ele pertence. */
export function alvoDoPainel(id: string): { folha: 'f1' | 'f2' | 'fixo'; selecao: Selecao } | null {
    const { folha, base } = folhaDoPainel(id);
    const selecao = selecaoDoPainel(base);
    if (!selecao) return null;
    // Chapa: guarda de qual lado (A ou B) foi o clique, para o 2D e o painel irem para aquele lado
    const lado = ladoDoPainel(base);
    return { folha, selecao: selecao.tipo === 'chapa' && lado ? { ...selecao, lado } : selecao };
}

/** Lado (A = frente, B = verso) de um painel do 3D, quando o id diz. */
export function ladoDoPainel(idBase: string): 'A' | 'B' | null {
    const m = /^(?:chapa|ripa|fresa|puxador-ripa)-([AB])/.exec(idBase);
    return m ? (m[1] as 'A' | 'B') : null;
}

/** O painel 3D (id de `construirCena3D`) faz parte da seleção atual (na folha ativa)? Usado para destacá-lo no 3D. */
export function painelNaSelecao(idCompleto: string, sel: Selecao | null | undefined, folhaAtiva: string = 'f1'): boolean {
    if (!sel) return false;
    const { folha, base: id } = folhaDoPainel(idCompleto);
    if (folha !== folhaAtiva) return false;
    if (sel.tipo === 'chapa') {
        const r = sel.rotulo.replace(/\./g, '\\.');
        const L = sel.lado ?? '[AB]'; // com o lado em foco, só o painel daquele lado fica destacado
        return new RegExp(`^(chapa-${L}${r}|(ripa|fresa)-${L}${r}-\\d+)$`).test(id) || (sel.rotulo === '1' && new RegExp(`^puxador-ripa-${L}$`).test(id));
    }
    if (sel.tipo === 'cava') return id.startsWith('cava-');
    if (sel.tipo === 'friso') return new RegExp(`^friso-fundo-[AB]-${sel.indice}(-|$)`).test(id);
    return false;
}
